<template>
  <!--
    Contract cohort assignments for quality measures: which measure is assigned to
    which contract cohort, with weight, target rate and scoring tiers.

    Rendered as the "Contract Assignments" sub-tab of the Metric Studio Quality
    Metrics artifact (registry `managerTab`), so the chrome deliberately matches
    ArtifactList: same info banner, toolbar, table styling, and row affordances.
  -->
  <div class="h-full overflow-y-auto ms2-scroll">
    <div class="p-4 flex flex-col">

      <!-- info banner (matches ArtifactList) -->
      <div
        class="flex items-start gap-2.5 mb-3 px-3.5 py-2.5 rounded-lg border text-xs leading-relaxed"
        :style="{ background: ACCENT + '0D', borderColor: ACCENT + '33', color: '#374151' }"
      >
        <font-awesome-icon :icon="['fas', 'circle-info']" class="mt-0.5 flex-shrink-0" :style="{ color: ACCENT }" />
        <span>
          Assign quality measures to contract cohorts. Weight drives composite scoring,
          target rate sets the year-end goal, and tiers map achieved rates to scores.
          Removing an assignment expires its opportunities on the next nightly sync.
        </span>
      </div>

      <!-- toolbar (matches ArtifactList) -->
      <div class="flex items-center gap-3 px-1 pb-3">
        <div class="relative flex-1 max-w-sm">
          <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
          <input
            v-model="query"
            type="text"
            placeholder="Search assignments…"
            class="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
          />
        </div>
        <select v-model="yearFilter" class="text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white text-gray-700">
          <option :value="null">All years</option>
          <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
        </select>
        <select v-model="contractFilter" class="text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white text-gray-700 max-w-[16rem]">
          <option :value="null">All contract cohorts</option>
          <option v-for="name in contractNames" :key="name" :value="name" class="sensitive">{{ name }}</option>
        </select>
        <select v-model="measureFilter" class="text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white text-gray-700 max-w-[16rem]">
          <option :value="null">All measures</option>
          <option v-for="name in measureNames" :key="name" :value="name">{{ name }}</option>
        </select>
        <button
          class="text-xs rounded-lg px-2.5 py-2 border whitespace-nowrap"
          :class="staleOnly
            ? 'bg-red-50 border-red-200 text-red-700 font-semibold'
            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'"
          :title="'Measures not on their contract\'s latest report'"
          @click="staleOnly = !staleOnly"
        >
          Not current
          <span v-if="staleCount" class="ml-1 text-[10px] opacity-70">{{ staleCount }}</span>
        </button>
        <span class="text-[11px] text-gray-400 whitespace-nowrap">
          {{ filteredEntityMetrics.length }} of {{ allEntityMetrics.length }}
        </span>
        <div class="flex-1"></div>
        <button
          class="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-2 hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
          :style="{ background: ACCENT }"
          :disabled="adding"
          @click="openAddModal"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="text-[10px]" />
          New Assignment
        </button>
      </div>

      <div v-if="loadingAssignments" class="text-[11px] text-gray-500 px-1 py-6">Loading…</div>
      <div v-else-if="error" class="text-[11px] text-red-600 px-1 py-6">{{ error }}</div>

      <!-- table (matches ArtifactList chrome) -->
      <div v-else class="overflow-auto bg-white border border-gray-200 rounded-xl" style="max-height: calc(100vh - 260px);">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                class="px-3 py-2 text-[11px] font-semibold text-gray-600 whitespace-nowrap select-none"
                :class="[col.width, col.sortable ? 'cursor-pointer hover:text-gray-900' : '', col.center ? 'text-center' : 'text-left']"
                @click="col.sortable && toggleSort(col.key)"
              >
                {{ col.label }}
                <span v-if="col.sortable && sortKey === col.key" class="text-gray-400">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th class="px-2 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="em in filteredEntityMetrics" :key="em.id" class="hover:bg-gray-50">
              <td class="sensitive px-3 py-1.5 text-xs text-gray-800 max-w-xs truncate" data-spoof-kind="contract" :title="em.entity_name">
                {{ em.entity_name }}
              </td>
              <td class="px-3 py-1.5 text-xs text-gray-800 max-w-xs truncate" :title="em.metric_name">
                {{ em.metric_name }}
              </td>
              <td class="px-3 py-1.5 text-xs">
                <span
                  v-if="em._payer_measure_code"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs border leading-tight font-mono bg-amber-50 text-amber-700 border-amber-200 max-w-[14rem] truncate"
                  :title="em._payer_measure_code"
                >{{ em._payer_measure_code }}</span>
                <span v-else class="text-gray-300">—</span>
              </td>
              <td class="px-3 py-1.5">
                <input
                  v-model.number="em.weight"
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  class="w-16 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                  @blur="updateEntityMetric(em)"
                />
              </td>
              <td class="px-3 py-1.5">
                <div class="flex items-center gap-1">
                  <input
                    v-model.number="em.target_rate_display"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    placeholder="—"
                    class="w-14 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                    @blur="updateTargetRate(em)"
                  />
                  <span class="text-xs text-gray-400">%</span>
                </div>
              </td>
              <td class="px-3 py-1.5 whitespace-nowrap">
                <span
                  class="inline-flex items-center gap-1.5 text-xs"
                  :title="freshnessTitle(em)"
                >
                  <span
class="w-2 h-2 rounded-full shrink-0" :class="{
                    'bg-green-500':  freshnessFor(em).status === 'current',
                    'bg-amber-400':  freshnessFor(em).status === 'behind',
                    'bg-red-500':    freshnessFor(em).status === 'stale',
                    'bg-red-300':    freshnessFor(em).status === 'missing',
                    'bg-gray-200':   freshnessFor(em).status === 'unknown',
                  }"></span>
                  <span :class="freshnessFor(em).last ? 'text-gray-700' : 'text-gray-300'">
                    {{ freshnessFor(em).last || 'Never' }}
                  </span>
                </span>
              </td>
              <td class="px-3 py-1.5">
                <div class="flex items-center gap-1.5">
                  <div class="flex gap-1 flex-wrap">
                    <span
                      v-for="(tier, i) in getTiers(em)"
                      :key="i"
                      class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border bg-gray-50 text-gray-600 border-gray-200 whitespace-nowrap"
                    >{{ tier.label }}: {{ formatTierRate(tier) }}</span>
                    <span v-if="!getTiers(em).length" class="text-gray-300 text-xs">—</span>
                  </div>
                  <button
                    class="text-gray-300 hover:text-gray-700 shrink-0"
                    title="Edit scoring tiers"
                    @click="openTierEditor(em)"
                  >
                    <font-awesome-icon :icon="['fas', 'pen-to-square']" class="text-[11px]" />
                  </button>
                </div>
              </td>
              <td class="px-3 py-1.5 text-center">
                <button
                  class="text-sm leading-none cursor-pointer"
                  :class="em._is_priority ? 'text-amber-400' : 'text-gray-200 hover:text-amber-300'"
                  title="Toggle priority"
                  @click="togglePriority(em)"
                >
                  <font-awesome-icon :icon="[em._is_priority ? 'fas' : 'far', 'star']" />
                </button>
              </td>
              <td class="px-2 py-1.5 text-right">
                <button
                  class="text-gray-300 hover:text-red-500 text-sm leading-none"
                  title="Remove assignment"
                  @click="confirmRemove = em"
                >×</button>
              </td>
            </tr>
            <tr v-if="!filteredEntityMetrics.length">
              <td :colspan="columns.length + 1" class="px-3 py-8 text-center text-xs text-gray-400">
                {{ allEntityMetrics.length ? 'No matches.' : 'No contract cohort assignments yet.' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Add assignment -->
    <transition
      enter-active-class="transition ease-out duration-300" enter-from-class="opacity-0" enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-200" leave-from-class="opacity-100" leave-to-class="opacity-0"
    >
      <div
        v-if="showAddModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-200 p-4"
        @click.self="closeAddModal"
      >
        <div class="bg-white rounded-lg shadow-2xl max-w-xl w-full mx-auto overflow-hidden max-h-[90vh] flex flex-col">
          <div class="bg-primary-100 px-6 py-4 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900">New Assignment</h3>
            <button class="text-gray-600 hover:text-gray-900 px-2" @click="closeAddModal">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>

          <div class="px-6 py-5 space-y-4 overflow-y-auto ms2-scroll">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Contract cohort <span class="text-red-dark">*</span>
              </label>
              <EntitySearch_Single
                button-text="Select contract cohort…"
                modal-title="Select Contract Cohort"
                :multiple="false"
                @entities-selected="onAddEntitySelected"
              />
              <div v-if="addEntity" class="mt-2 inline-flex items-center gap-2 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded text-xs">
                <span class="text-gray-800">{{ addEntity.name }}</span>
                <button class="text-gray-400 hover:text-gray-700" @click="addEntity = null">&times;</button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Quality measure <span class="text-red-dark">*</span>
              </label>
              <select
                v-model.number="addMetricId"
                class="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none"
              >
                <option :value="null">Select measure…</option>
                <option v-for="m in qualityMetrics" :key="m.id" :value="m.id">
                  {{ m.name }} ({{ m.measure_code }})
                </option>
              </select>
            </div>

            <div class="flex gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                <input
                  v-model.number="addWeight"
                  type="number" min="0" max="10" step="0.1"
                  class="w-28 rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Target rate</label>
                <div class="flex items-center gap-1">
                  <input
                    v-model.number="addTargetRateDisplay"
                    type="number" min="0" max="100" step="1" placeholder="—"
                    class="w-24 rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none"
                  />
                  <span class="text-sm text-gray-400">%</span>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Scoring tiers</label>
              <TierTable v-model="addTiers" />
            </div>

            <p v-if="addDuplicate" class="text-sm text-red-dark">
              This measure is already assigned to that contract cohort.
            </p>
          </div>

          <div class="px-6 pb-6 pt-4 flex gap-2 justify-end">
            <button class="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded" @click="closeAddModal">
              Cancel
            </button>
            <button
              class="text-sm bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded"
              :disabled="!canAdd || adding"
              @click="saveNewAssignment"
            >{{ adding ? 'Adding…' : 'Add Assignment' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Edit tiers -->
    <transition
      enter-active-class="transition ease-out duration-300" enter-from-class="opacity-0" enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-200" leave-from-class="opacity-100" leave-to-class="opacity-0"
    >
      <div
        v-if="tierEditorEm"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-200 p-4"
        @click.self="tierEditorEm = null"
      >
        <div class="bg-white rounded-lg shadow-2xl max-w-lg w-full mx-auto overflow-hidden">
          <div class="bg-primary-100 px-6 py-4 flex items-start justify-between">
            <div class="min-w-0">
              <h3 class="text-lg font-semibold text-gray-900">Scoring Tiers</h3>
              <p class="text-xs text-gray-600 truncate">
                <span class="sensitive" data-spoof-kind="contract">{{ tierEditorEm.entity_name }}</span> — {{ tierEditorEm.metric_name }}
              </p>
            </div>
            <button class="text-gray-600 hover:text-gray-900 px-2" @click="tierEditorEm = null">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>
          <div class="px-6 py-5">
            <TierTable v-model="tierEditorTiers" />
          </div>
          <div class="px-6 pb-6 flex gap-2 justify-end">
            <button class="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded" @click="tierEditorEm = null">
              Cancel
            </button>
            <button
              class="text-sm bg-primary-500 hover:bg-primary-600 text-white font-medium px-4 py-2 rounded"
              @click="saveTiers"
            >Save Tiers</button>
          </div>
        </div>
      </div>
    </transition>

    <ConfirmationModal
      :is-visible="!!confirmRemove"
      :message="confirmRemove
        ? `Remove ${confirmRemove.metric_name} from ${confirmRemove.entity_name}? Opportunities expire on the next nightly sync.`
        : ''"
      @confirm="doRemove"
      @cancel="confirmRemove = null"
    />

    <div
      v-if="successMessage"
      class="fixed bottom-4 right-4 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-700 shadow-lg z-50"
    >{{ successMessage }}</div>
  </div>
</template>

<script>
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import EntitySearch_Single from '@/components/widgetcomponents/Search_Widgets/EntitySearch_Single.vue'
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue'
import TierTable from './TierTable.vue'

export default {
  name: 'QualityMetricSetup',

  components: {
    FontAwesomeIcon,
    EntitySearch_Single,
    ConfirmationModal,
    TierTable,
  },

  data() {
    return {
      // matches the Quality artifact accent in artifactRegistry.js
      ACCENT: '#059669',

      // Loading states
      loadingMetrics: false,
      loadingAssignments: false,
      adding: false,

      // Data
      qualityMetrics: [],
      allEntityMetrics: [],

      // Add modal
      showAddModal: false,
      addEntity: null,
      addMetricId: null,
      addWeight: 1.0,
      addTargetRateDisplay: null,
      addTiers: [],
      addDuplicate: false,

      // Snowflake measure names for payer_measure_code dropdown
      snowflakeMeasureNames: [],

      // cpy_id -> { contract_id, contract_last_report, measures{}, cycles[] }
      freshness: {},
      staleOnly: false,

      // Assignment filter
      query: '',
      yearFilter: null,
      contractFilter: null,
      measureFilter: null,
      sortKey: 'entity_name',
      sortDir: 'asc',

      // Remove confirmation
      confirmRemove: null,

      // Tier editor (existing rows)
      tierEditorEm: null,
      tierEditorTiers: [],

      // Messages
      error: null,
      successMessage: null
    }
  },

  computed: {
    canAdd() {
      return !!this.addEntity && !!this.addMetricId
    },
    years() {
      const years = [...new Set(this.allEntityMetrics.map(em => {
        const match = em.entity_name && em.entity_name.match(/\b(20\d{2})\b/)
        return match ? match[1] : null
      }).filter(Boolean))].sort().reverse()
      return years
    },
    yearFilteredEntityMetrics() {
      if (!this.yearFilter) return this.allEntityMetrics
      return this.allEntityMetrics.filter(em => em.entity_name && em.entity_name.includes(this.yearFilter))
    },
    contractNames() {
      return [...new Set(this.yearFilteredEntityMetrics.map(em => em.entity_name))].sort()
    },
    measureNames() {
      return [...new Set(this.yearFilteredEntityMetrics.map(em => em.metric_name))].sort()
    },
    filteredEntityMetrics() {
      let list = this.yearFilteredEntityMetrics
      if (this.contractFilter) {
        list = list.filter(em => em.entity_name === this.contractFilter)
      }
      if (this.measureFilter) {
        list = list.filter(em => em.metric_name === this.measureFilter)
      }
      const q = this.query.trim().toLowerCase()
      if (q) {
        list = list.filter(em =>
          [em.entity_name, em.metric_name, em._payer_measure_code]
            .some(v => String(v || '').toLowerCase().includes(q))
        )
      }
      if (this.staleOnly) {
        list = list.filter(em => {
          const s = this.freshnessFor(em).status
          return s === 'behind' || s === 'stale' || s === 'missing'
        })
      }
      const dir = this.sortDir === 'asc' ? 1 : -1
      return list.slice().sort((a, b) => {
        // Last Report isn't stored on the row; derive it for sorting. Never
        // delivered sorts as oldest rather than as empty.
        const key = this.sortKey
        const val = (r) => key === '_last_report'
          ? (this.freshnessFor(r).last || '0000-00-00')
          : r[key]
        const va = val(a)
        const vb = val(b)
        const ae = va === null || va === undefined || va === ''
        const be = vb === null || vb === undefined || vb === ''
        if (ae && be) return 0
        if (ae) return 1
        if (be) return -1
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        return String(va).localeCompare(String(vb)) * dir
      })
    },
    columns() {
      return [
        { key: 'entity_name', label: 'Contract Cohort', sortable: true },
        { key: 'metric_name', label: 'Measure', sortable: true },
        { key: '_payer_measure_code', label: 'Payer Measure Code', sortable: true, width: 'w-56' },
        { key: 'weight', label: 'Weight', sortable: true, width: 'w-24' },
        { key: 'target_rate_display', label: 'Target', sortable: true, width: 'w-28' },
        { key: '_last_report', label: 'Last Report', sortable: true, width: 'w-40' },
        { key: 'tiers', label: 'Tiers', width: 'w-56' },
        { key: '_is_priority', label: 'Priority', sortable: true, width: 'w-20', center: true },
      ]
    },
    staleCount() {
      return this.allEntityMetrics.filter(em => {
        const s = this.freshnessFor(em).status
        return s === 'behind' || s === 'stale' || s === 'missing'
      }).length
    }
  },

  watch: {
    yearFilter() {
      this.contractFilter = null
      this.measureFilter = null
    }
  },

  async mounted() {
    await Promise.all([
      this.loadQualityMetrics(),
      this.loadAllEntityMetrics(),
      this.loadSnowflakeMeasureNames(),
      this.loadFreshness(),
    ])
  },

  methods: {
    // Data loading
    async loadQualityMetrics() {
      this.loadingMetrics = true
      try {
        const response = await axios.get('/api/metrics/', {
          params: { is_quality_measure: 'true' }
        })
        this.qualityMetrics = response.data.results || response.data
      } catch (err) {
        console.error('Error loading quality metrics:', err)
        this.error = 'Failed to load quality metrics'
      } finally {
        this.loadingMetrics = false
      }
    },

    async loadAllEntityMetrics() {
      this.loadingAssignments = true
      try {
        // Only load active EntityMetrics (is_active=true)
        const response = await axios.get('/api/entitymetrics/', {
          params: { is_active: 'true' }
        })
        const data = response.data.results || response.data
        console.log(data);
        // Add display fields for UI
        this.allEntityMetrics = data.map(em => ({
          ...em,
          target_rate_display: em.target_rate != null ? Math.round(em.target_rate * 100) : null,
          _payer_measure_code: (em.config || {}).payer_measure_code || '',
          _is_priority: !!(em.config || {}).is_priority,
        })).sort((a, b) => (a.entity_name || '').localeCompare(b.entity_name || '') || (a.metric_name || '').localeCompare(b.metric_name || ''))
      } catch (err) {
        console.error('Error loading entity metrics:', err)
        this.error = 'Failed to load entity metrics'
      } finally {
        this.loadingAssignments = false
      }
    },

    async loadFreshness() {
      try {
        const response = await axios.get('/api/quality/measure-freshness/')
        this.freshness = response.data || {}
      } catch (err) {
        // Non-fatal: the tab is still usable without the staleness columns.
        console.error('Error loading measure freshness:', err)
        this.freshness = {}
      }
    },

    // Staleness is measured against the CONTRACT's own newest report, not the
    // calendar. Payers deliver monthly, so everything is 50-80 days old at any
    // moment; what matters is a measure lagging the file its payer just sent.
    freshnessTitle(em) {
      const f = this.freshnessFor(em)
      const blk = this.freshness[String(em.entity)]
      if (!blk) return 'No report data for this contract'
      const code = em.measure_code
      if (f.status === 'missing') {
        return `${code}: never delivered by this payer.\n`
          + `Contract's latest report: ${blk.contract_last_report}.\n`
          + 'Either the payer does not send this measure, or the configured '
          + 'code does not match what the feed emits.'
      }
      return `${code}: last reported ${f.last}\n`
        + `Contract's latest report: ${blk.contract_last_report}\n`
        + f.label
    },

    freshnessFor(em) {
      // JSON object keys are always strings, so entity_id (a number) must be
      // coerced or every lookup misses and every row reads "Never".
      const blk = this.freshness[String(em.entity)]
      if (!blk) return { status: 'unknown', last: null, label: '—' }
      // Join on the STANDARD code, not payer_measure_code. The latter is the
      // payer's own raw measure NAME ("Breast Cancer Screening-ECDS") and only
      // coincides with the HEDIS code in 61 of 535 assignments; the compliance
      // view is keyed on HEDIS_MEASURE_CODE.
      const code = em.measure_code || em.metric_measure_code
      const last = (code && blk.measures[code]) || null
      if (!last) {
        return { status: 'missing', last: null, cyclesBehind: null,
                 label: 'Never delivered' }
      }
      // How many of the contract's report cycles this measure sits behind.
      // A date NOT in cycles means it came from an earlier performance year via
      // the per-measure fallback (blk.fallback_years), so the contract's current
      // cycle list cannot rank it. That is the WORST case, not an unknown one:
      // the payer has sent whole files since without this measure.
      const idx = (blk.cycles || []).indexOf(last)
      const fellBack = !!(blk.fallback_years || {})[code]
      const behind = idx < 0 ? null : idx
      let status = 'current'
      if (behind === null) status = fellBack ? 'stale' : 'unknown'
      else if (behind === 1) status = 'behind'
      else if (behind >= 2) status = 'stale'
      return { status, last, cyclesBehind: behind,
               label: behind ? `${behind} report${behind > 1 ? 's' : ''} behind` : 'Current' }
    },

    async loadSnowflakeMeasureNames() {
      try {
        const response = await axios.get('/api/quality/available-measures/', {
          params: { include_existing: 'true' }
        })
        this.snowflakeMeasureNames = [...new Set(
          response.data.map(m => m.measure_name).filter(Boolean)
        )].sort()
      } catch (err) {
        console.error('Error loading Snowflake measure names:', err)
      }
    },

    // Add modal
    openAddModal() {
      this.addEntity = null
      this.addMetricId = null
      this.addWeight = 1.0
      this.addTargetRateDisplay = null
      this.addTiers = []
      this.addDuplicate = false
      this.showAddModal = true
    },

    closeAddModal() {
      this.showAddModal = false
    },

    onAddEntitySelected(entities) {
      this.addEntity = entities && entities.length > 0 ? entities[0] : null
      this.addDuplicate = false
    },

    async saveNewAssignment() {
      if (!this.canAdd) return

      this.addDuplicate = this.allEntityMetrics.some(
        em => em.entity === this.addEntity.id && em.metric === this.addMetricId
      )
      if (this.addDuplicate) return

      this.adding = true
      this.error = null
      try {
        const targetRate = this.addTargetRateDisplay != null && this.addTargetRateDisplay !== ''
          ? this.addTargetRateDisplay / 100
          : null
        // addTiers already arrives in stored (0-1) form from TierTable.
        const tiers = this.addTiers.filter(t => t.label)

        await axios.post('/api/entitymetrics/', {
          entity: this.addEntity.id,
          metric: this.addMetricId,
          weight: this.addWeight,
          target_rate: targetRate,
          config: tiers.length > 0 ? { tiers } : {}
        })

        this.showSuccess('Assignment added')
        this.showAddModal = false
        await this.loadAllEntityMetrics()
      } catch (err) {
        console.error('Error adding assignment:', err)
        this.error = err.response?.data?.error || 'Failed to add assignment'
      } finally {
        this.adding = false
      }
    },

    // Entity metric actions
    async updateEntityMetric(em) {
      try {
        await axios.patch(`/api/entitymetrics/${em.id}/`, {
          weight: em.weight
        })
        this.showSuccess('Updated')
      } catch (err) {
        console.error('Error updating:', err)
        this.error = 'Failed to update'
      }
    },

    async updateTargetRate(em) {
      try {
        // Convert display value (0-100) to stored value (0-1)
        const targetRate = em.target_rate_display != null && em.target_rate_display !== ''
          ? em.target_rate_display / 100
          : null
        await axios.patch(`/api/entitymetrics/${em.id}/`, {
          target_rate: targetRate
        })
        this.showSuccess('Target rate updated')
      } catch (err) {
        console.error('Error updating target rate:', err)
        this.error = 'Failed to update target rate'
      }
    },

    async doRemove() {
      const em = this.confirmRemove
      this.confirmRemove = null
      if (!em) return
      try {
        // Soft-delete: set is_active=false and deactivated_at
        await axios.patch(`/api/entitymetrics/${em.id}/`, {
          is_active: false,
          deactivated_at: new Date().toISOString()
        })
        this.allEntityMetrics = this.allEntityMetrics.filter(e => e.id !== em.id)
        this.showSuccess('Removed - opportunities will expire on next nightly sync')
      } catch (err) {
        console.error('Error removing:', err)
        this.error = 'Failed to remove'
      }
    },

    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }
    },
    getTiers(em) {
      return (em.config || {}).tiers || [];
    },
    formatTierRate(tier) {
      if (tier.min_rate != null) return (tier.min_rate * 100).toFixed(1) + '%';
      if (tier.max_rate != null) return '≤' + (tier.max_rate * 100).toFixed(1) + '%';
      return '—';
    },
    async togglePriority(em) {
      const newVal = !em._is_priority;
      em._is_priority = newVal;
      try {
        const config = { ...(em.config || {}) };
        config.is_priority = newVal || undefined;
        await axios.patch(`/api/entitymetrics/${em.id}/`, { config });
        em.config = config;
      } catch (err) {
        em._is_priority = !newVal;
        console.error('Error updating priority:', err);
        this.error = 'Failed to update priority';
      }
    },
    async updateConfig(em) {
      try {
        const config = { ...(em.config || {}) };
        config.payer_measure_code = em._payer_measure_code || undefined;
        await axios.patch(`/api/entitymetrics/${em.id}/`, { config });
        em.config = config;
        this.showSuccess('Updated');
      } catch (err) {
        console.error('Error updating config:', err);
        this.error = 'Failed to update';
      }
    },
    openTierEditor(em) {
      this.tierEditorEm = em;
      this.tierEditorTiers = JSON.parse(JSON.stringify((em.config || {}).tiers || []));
    },
    async saveTiers() {
      const em = this.tierEditorEm;
      if (!em) return;
      try {
        const config = { ...(em.config || {}) };
        config.tiers = this.tierEditorTiers.filter(t => t.label);
        await axios.patch(`/api/entitymetrics/${em.id}/`, { config });
        em.config = config;
        this.tierEditorEm = null;
        this.tierEditorTiers = [];
        this.showSuccess('Tiers updated');
      } catch (err) {
        console.error('Error saving tiers:', err);
        this.error = 'Failed to save tiers';
      }
    },

    showSuccess(message) {
      this.successMessage = message
      setTimeout(() => { this.successMessage = null }, 2000)
    }
  }
}
</script>

<style scoped>
.ms2-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.ms2-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 10px; border: 3px solid transparent; background-clip: content-box; }
.ms2-scroll::-webkit-scrollbar-track { background: transparent; }
</style>
